// ── a place's address, as analytics may know it ──────────────────────────────
//
// On the stage every directory location is its own url (lib/stage/location):
//
//   /dashboard/projects/<project-id>/<slug-of-a-dimension-title>
//
// The last segment is the SLUG OF A TITLE, and titles are what the whole privacy
// story keeps off-site: replay blocks the rows that carry them, autocapture
// drops their text. A url is the one place that story did not reach, because
// until the stage a url never held one. posthog-js records the location on every
// event (one $pageview per pushState, `$current_url`, `$pathname`, the session's
// entry url, the autocapture chain of a clicked link) and Sentry records it on
// every breadcrumb. So location now leaves the browser as a TEMPLATE:
//
//   /dashboard/projects/<id>/<title-slug>   →   /dashboard/projects/:project/:place
//   /dashboard/projects/<id>                →   /dashboard/projects/:project
//   /dashboard/you/<anything>               →   /dashboard/you/:place
//   /dashboard/you                          →   /dashboard/you          (a section is a fixed name, not a title)
//   /mirror/…                               →   the same, under /mirror
//   everything else                         →   untouched
//
// The depth under a place collapses too: how deeply someone nested a document is
// a fact about their memory, and ":place" says only that they were in one.
//
// This lives apart from instrumentation-client.ts for the reason replayMask.ts
// does: that module calls posthog.init() on import, and a privacy guarantee this
// load-bearing has to be testable without starting analytics. It is pure and
// imports nothing. scrubEvent.ts composes it into the hooks; lib/stage/place.ts
// is the server half, the one that reads these same addresses.

/** the stage bases whose places carry titles (lib/stage/place `BASES`). */
const BASES: readonly string[] = ['dashboard', 'mirror']

export const PROJECT_SLOT = ':project'
export const PLACE_SLOT = ':place'

/**
 * The segments of a shell path ("projects", "p1", "vision"), templated. Returns
 * the SAME array when there is nothing to template (a section, or nothing), so a
 * caller can tell by identity and hand the original string back untouched.
 */
function templateSegments(segs: string[]): string[] {
  const [section, ...below] = segs
  if (!below.length) return segs
  if (section === 'projects') {
    return below.length === 1 ? [section, PROJECT_SLOT] : [section, PROJECT_SLOT, PLACE_SLOT]
  }
  return [section, PLACE_SLOT]
}

/**
 * A shell path ("/projects/p1/vision", the `?open=` of an arrival link) as its
 * template. Anything that is not a path, or has nothing below its section, comes
 * back as it came.
 */
export function templateShellPath(shellPath: string): string {
  if (!shellPath.startsWith('/')) return shellPath
  const segs = shellPath.split('/').filter(Boolean)
  const out = templateSegments(segs)
  return out === segs ? shellPath : '/' + out.join('/')
}

/**
 * A url PATHNAME as its template: `/dashboard|mirror` + a place becomes the
 * shape of the place, every other path comes back unchanged.
 *
 * A `?query` or `#hash` riding on the string is kept exactly as given (this is
 * what lets it template a relative url too, the form Sentry's navigation
 * breadcrumbs use). Cleaning the query is scrubEvent's job, not this one's.
 */
export function templatePlacePath(pathname: string): string {
  const cut = pathname.search(/[?#]/)
  const path = cut < 0 ? pathname : pathname.slice(0, cut)
  const tail = cut < 0 ? '' : pathname.slice(cut)
  const segs = path.split('/') // ["", "dashboard", "projects", …]
  if (segs[0] !== '' || !BASES.includes(segs[1])) return pathname
  const rest = segs.slice(2).filter(Boolean) // a trailing or doubled slash is no segment
  const out = templateSegments(rest)
  return out === rest ? pathname : `/${segs[1]}/${out.join('/')}${tail}`
}

/**
 * A node's key as its template (`data-node-key`, `data-nav-key`: a recording
 * stores them verbatim). Keys are made of the same words as paths (lib/os/tree):
 * `dim:<title-slug>`, `space:<room-slug>`, `node:<id>`, a project's bare id. A
 * section or a panel is a fixed lowercase word ("you", "projects", "root") and
 * keeps its name; any other key keeps only its kind.
 */
export function templateNodeKey(key: string): string {
  if (/^[a-z]+$/.test(key)) return key
  const colon = key.indexOf(':')
  // a bare key that is not a word is a project's id (lib/os/tree projectFolderNode)
  return colon > 0 ? `${key.slice(0, colon)}:${PLACE_SLOT}` : PROJECT_SLOT
}

// A place path anywhere inside a string: a bare `/dashboard/…`, or one behind an
// origin (`https://innernet.live/dashboard/…`). The character in front of the
// path keeps `/api/you/mirror/…` out of it, and a path ends at whatever cannot
// be in one: whitespace, a quote (the autocapture chain wraps each attribute in
// them), a `?` or `#`, an angle bracket.
const PLACE_IN_TEXT = /(^|[^A-Za-z0-9_\-.~%/])((?:(?:https?:)?\/\/[^/\s"'<>]+)?)(\/(?:dashboard|mirror)(?:\/[^\s"'<>?#\\;]*)?)/g

// A query pair inside a string, or AT its start (a bare query string: Sentry's
// `request.query_string`, the `url.query` span attribute). A place rides in one
// two ways:
//   `?open=<shell path>`: how an arrival link names a place (a forget preview,
//     a handoff, the repo rail): `/dashboard?open=%2Fprojects%2F<id>%2F<title-slug>`.
//   any other key holding an address: the proxy sends a signed-out visitor to
//     `/login?next=%2Fdashboard%2Fprojects%2F<id>%2F<title-slug>`, the shell's
//     own "sign in" link is that same url, and /welcome nests one next in another.
// Encoded, a place has no `/dashboard` for PLACE_IN_TEXT to see, so each value
// is decoded once and looked at again, as text of its own.
const QUERY_IN_TEXT = /((?:^|[?&])([A-Za-z0-9_.~%-]+)=)([^&#\s"'<>\\;]*)/g

// how many layers of `next=` inside `next=` are opened. Each layer decodes once,
// so this is also how many times over a place can be encoded and still be found.
const MAX_NEST = 4

function templateText(text: string, depth: number): string {
  // 'open' and not 'open=': a nested arrival link is `open%3D…` until decoded
  if (!text.includes('dashboard') && !text.includes('mirror') && !text.includes('open')) return text
  return text
    .replace(PLACE_IN_TEXT, (_m, pre: string, origin: string, path: string) => pre + origin + templatePlacePath(path))
    .replace(QUERY_IN_TEXT, (whole, head: string, key: string, value: string) => {
      let decoded = value
      try {
        decoded = decodeURIComponent(value)
      } catch {
        /* a malformed escape: template the raw text */
      }
      const out = key === 'open' ? templateShellPath(decoded) : depth < MAX_NEST ? templateText(decoded, depth + 1) : decoded
      return out === decoded ? whole : head + encodeURIComponent(out)
    })
}

/**
 * Every place path inside `text`, templated: the free-text twin of
 * `templatePlacePath`, for the values nobody can name a key for (an autocapture
 * element chain, an exception message, a breadcrumb, a bare query string). A
 * place inside a query value is found encoded or not (`?next=%2Fdashboard…`).
 * Text with no place in it is returned as is, after one cheap look.
 */
export function templatePlacesInText(text: string): string {
  return templateText(text, 0)
}
